import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Wordmark } from "../components/Wordmark";

type State = "idle" | "working" | "done" | "error";

/**
 * The only route into a company. Reached from the emailed link.
 * The token is verified in the database, which also checks that the signed-in
 * address matches the one invited — so forwarding the email to a colleague
 * does not give them access.
 */
export function AcceptInvite({ token, onAccepted }: { token: string; onAccepted: () => void }) {
  const [state, setState] = useState<State>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSignedIn(Boolean(data.session)));
  }, []);

  async function accept() {
    setState("working");
    const { error } = await supabase.rpc("accept_invitation", { p_token: token });
    if (error) {
      setState("error");
      setMessage(
        error.message.includes("different address")
          ? "This invitation was sent to a different email address. Sign in as that address to accept it."
          // Was: "It may have been used already, or it may have expired."
          // Whoever holds this link may not be who it was sent to, and which of
          // the two it is tells them something about the account's activity.
          : "This invitation is no longer valid. Ask for a new one.",
      );
      return;
    }
    setState("done");
    // Clear the token from the URL so it is not left in history or a screenshot.
    window.history.replaceState({}, "", "/");
    onAccepted();
  }

  if (signedIn === null) return <main className="auth"><p className="muted">Loading…</p></main>;

  return (
    <main className="auth">
      <div className="card">
        {/* This is the first screen anyone sees, before they have an account.
            It carried no mark at all, so an emailed link opened on a page that
            could have belonged to anybody. */}
        <div className="signin-mark"><Wordmark height={48} /></div>
        <h1>You have been invited</h1>
        {signedIn ? (
          <>
            <p className="muted">
              Accepting adds this account to the company. You can be a member of more than one.
            </p>
            {state !== "error" && (
              <button className="btn" onClick={accept} disabled={state === "working"}>
                {state === "working" ? "Accepting…" : "Accept invitation"}
              </button>
            )}
          </>
        ) : (
          <p className="muted">
            Sign in first with the address the invitation was sent to, then open the link again.
          </p>
        )}
        {message && <p className="status">{message}</p>}
        {!signedIn && <SignInPrompt />}
      </div>
    </main>
  );
}

function SignInPrompt() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  return (
    <>
      <label htmlFor="ie">Email</label>
      <input id="ie" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <button
        className="btn"
        disabled={!email || sent}
        onClick={async () => {
          await supabase.auth.signInWithOtp({
            email,
            options: { emailRedirectTo: window.location.href },
          });
          setSent(true);
        }}
      >
        {sent ? "Check your email" : "Email me a sign-in link"}
      </button>
    </>
  );
}
